/**
 * /dashboard/accounting/money/receipts/$id — Receipt detail (G_ACC6c).
 *
 * Migration note (Phase 3e — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/accounting/money/receipts/[id]/page.tsx.
 */

import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowLeft, ChevronRight } from "lucide-react";
import { toApiError } from "@/lib/api";
import { useReceipt } from "@/hooks/use-accounting";
import { AccountingModuleShell } from "@/components/shared/accounting-module-shell";
import { ReceiptDetail } from "@/components/accounting/receipt-detail";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/accounting/money/receipts/$id",
)({
  component: ReceiptDetailRoute,
});

function DetailSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-48 w-full" />
    </div>
  );
}

function ReceiptDetailRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { id } = Route.useParams();
  const numId = Number(id);
  const validId = Number.isFinite(numId) && numId > 0;
  const q = useReceipt(numId, validId);

  return (
    <AccountingModuleShell
      title="Receipt"
      description={q.data?.receipt_no}
      actions={
        <Button asChild variant="outline" className="gap-1.5">
          <Link to="/app/$tenantSlug/accounting/money/receipts" params={{ tenantSlug }}>
            <ArrowLeft aria-hidden className="h-4 w-4" />
            Back to receipts
          </Link>
        </Button>
      }
    >
      {!validId && (
        <Alert variant="destructive">
          <AlertTitle>Invalid receipt id</AlertTitle>
          <AlertDescription>
            The URL doesn&apos;t look like a valid receipt id.
          </AlertDescription>
        </Alert>
      )}

      {validId && q.isLoading && <DetailSkeleton />}

      {validId && q.isError && (
        <Alert variant="destructive">
          <AlertTitle>Could not load this receipt</AlertTitle>
          <AlertDescription>
            {toApiError(q.error).message}{" "}
            <Link
              to="/app/$tenantSlug/accounting/money/receipts" params={{ tenantSlug }}
              className="ml-1 inline-flex items-center gap-1 font-medium text-primary underline-offset-4 hover:underline"
            >
              Back to receipts
              <ChevronRight aria-hidden className="h-4 w-4" />
            </Link>
          </AlertDescription>
        </Alert>
      )}

      {validId && q.data && <ReceiptDetail receipt={q.data} />}
    </AccountingModuleShell>
  );
}
